import React, { useEffect, useRef, useState } from 'react';

import Tooltip from 'CommonComponents/Tooltip';

import CircleInfoIcon from 'Assets/icons/circle-info.svg';

let nextId = 0;

/**
 * An (i) button showing a figure's definition on hover, on keyboard focus, and
 * on tap. A tap pins it open, because a touch screen has no hover to hold it; a
 * second tap, a tap elsewhere or Escape closes it. The click never reaches the
 * column header, so reading a definition does not re-sort the table.
 *
 * The popup is portalled and only mounted while open, so the definition is
 * also kept in a hidden element beside the button for aria-describedby, which
 * still resolves it. Hidden rather than screen-reader-only, so the definition
 * does not also join the accessible name of the header or title around it.
 */
export default function TimeOverviewInfoTip({ label, definition, className = 'tw-text-charcoal-60' }) {
    const [hovered, setHovered] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [descriptionId] = useState(() => `time-overview-definition-${++nextId}`);
    const buttonRef = useRef(null);

    const visible = hovered || pinned;

    useEffect(() => {
        if (!visible) {
            return undefined;
        }

        const close = () => {
            setHovered(false);
            setPinned(false);
        };

        const closeOnOutsideClick = (event) => {
            if (buttonRef.current && !buttonRef.current.contains(event.target)) {
                close();
            }
        };

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        document.addEventListener('click', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.removeEventListener('click', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [visible]);

    return (
        <>
            <Tooltip
                tooltipType="content"
                placement="top"
                trigger={['hover', 'focus']}
                visible={visible}
                onVisibleChange={setHovered}
                closeOnOutsideClick={false}
                backgroundClass="tw-bg-charcoal"
                triggerRef={buttonRef}
                tooltipContent={
                    <div className="tw-max-w-[16rem] tw-text-start tw-text-xs tw-font-normal tw-text-white tw-whitespace-normal">
                        <div className="tw-font-semibold tw-mb-0.5">{label}</div>
                        {definition}
                    </div>
                }
            >
                <button
                    type="button"
                    aria-label={`About ${label}`}
                    aria-describedby={descriptionId}
                    className={`tw-inline-flex tw-shrink-0 tw-items-center tw-cursor-help ${className}`}
                    onClick={(event) => {
                        event.stopPropagation();

                        // A tap also fires the hover and focus that opened it,
                        // so closing has to clear those as well as the pin.
                        if (pinned) {
                            setPinned(false);
                            setHovered(false);
                        } else {
                            setPinned(true);
                        }
                    }}
                >
                    <CircleInfoIcon className="tw-w-3.5 tw-h-3.5" />
                </button>
            </Tooltip>
            <span id={descriptionId} hidden>
                {definition}
            </span>
        </>
    );
}
